import { useState } from 'react';
import type { ResearchState } from '@galaxis/shared';
import { AREAS, AREA_META, fmtDuration, num } from './research-format.ts';
import type { TechArea } from '@galaxis/shared';
import { formatEta } from '@galaxis/shared';

interface Props {
  state: ResearchState;
  now: number;
  busy: boolean;
  live: { metal: number; energy: number } | null;
  onUpgrade: (labId: string, tier: number) => void;
  onBuild: (systemId: string, systemName: string, area: TechArea) => void;
}

const BTN = 'btn-primary min-h-11 w-full text-xs disabled:opacity-40';
const BTN_GHOST = 'min-h-11 px-3 rounded border border-slate-600 text-xs text-slate-300 hover:border-slate-400 disabled:opacity-40';

/** Laboratories: each belongs to an area and produces that area's RP. */
export function LabsPanel({ state, now, busy, live, onUpgrade, onBuild }: Props) {
  const [buildArea, setBuildArea] = useState<TechArea>('physics');
  const areaState = state.areas.find(a => a.area === buildArea)!;
  const areaFull = areaState.labs.count >= areaState.labs.max;
  const afford = !!live && live.metal >= state.labBuild.metal && live.energy >= state.labBuild.energy;
  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-label="Laboratóriumok" data-testid="labs">
      <h2 className="text-sm font-semibold text-slate-300 mb-2">Laboratóriumok ({state.labs.length})</h2>
      <p className="text-[11px] text-slate-500 mb-2">Egy labor egy terület kutatási pontját termeli. Területenként legfeljebb 2 labor működhet; a 2. szintű labor ugyanabban a területben második kutatási helyet ad.</p>
      {state.labs.length === 0 && <p className="text-xs text-slate-500 mb-2">Még nincs laboratóriumod: az anyabolygó tudósai területenként csak 1 RP/órát adnak.</p>}
      <ul className="space-y-2">
        {state.labs.map(l => (
          <li key={l.id} className="rounded-lg border border-slate-700 p-3" data-testid={`lab-${l.systemName}`} data-area={l.area}>
            <div className="flex justify-between gap-2 text-sm">
              <span className="text-slate-200">{l.area ? AREA_META[l.area].icon : ''} {l.area ? AREA_META[l.area].label : ''}-labor · {l.systemName} · {l.tier}. szint</span>
              <span className="text-xs text-slate-400">
                {l.status === 'building' && l.completesAt ? `Épül: ${formatEta(l.completesAt, now)}` : l.status === 'upgrading' && l.completesAt ? `Fejlesztés: ${formatEta(l.completesAt, now)}` : `+${num(l.rpPerHour)} RP/ó`}
              </span>
            </div>
            <p className="text-[11px] text-slate-500">Fenntartás: {l.energyUpkeep} energia/ó</p>
            {l.next && (
              <div className="mt-2">
                <button type="button" className={BTN} data-testid={`upgrade-lab-${l.id}`}
                  disabled={busy || !!l.next.blockedBy || !live || live.metal < l.next.metal || live.energy < l.next.energy}
                  title={l.next.blockedBy ? `Előbb kell: ${l.next.blockedBy}` : undefined}
                  onClick={() => onUpgrade(l.id, l.next!.tier)}>
                  Fejlesztés {l.next.tier}. szintre — {l.next.metal} fém · {l.next.energy} en. · {fmtDuration(l.next.timeMs)} → +{num(l.next.rpPerHour)} RP/ó, fenntartás {l.next.energyUpkeep} en./ó
                </button>
                {l.next.blockedBy && <p className="text-[11px] text-amber-400 mt-1">Feltétel: {l.next.blockedBy}</p>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {state.labBuild.canBuild && (
        <div className="mt-3 space-y-2" data-testid="lab-sites">
          <p className="text-xs text-slate-400">Új laboratórium: {state.labBuild.metal} fém · {state.labBuild.energy} en. · {fmtDuration(state.labBuild.timeMs)} → +{num(state.labBuild.rpPerHour)} RP/ó, fenntartás {state.labBuild.energyUpkeep} en./ó (a megrendeléstől)</p>
          <div role="radiogroup" aria-label="A labor területe" className="flex flex-wrap gap-2">
            {AREAS.map(a => {
              const st = state.areas.find(x => x.area === a)!;
              const full = st.labs.count >= st.labs.max;
              return (
                <button key={a} type="button" role="radio" aria-checked={buildArea === a} disabled={full}
                  className={`min-h-11 px-3 rounded border text-xs ${buildArea === a ? 'border-accent text-accent' : 'border-slate-600 text-slate-300'} disabled:opacity-40`}
                  data-testid={`lab-area-${a}`} onClick={() => setBuildArea(a)}>
                  {AREA_META[a].icon} {AREA_META[a].label} ({st.labs.count}/{st.labs.max})
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            {state.labSites.map(s => (
              <button key={s.systemId} type="button" className={BTN_GHOST} disabled={busy || areaFull || !afford}
                title={areaFull ? 'Ebben a területben már a maximális számú labor működik' : !afford ? 'Nincs elég nyersanyag' : undefined}
                data-testid={`build-lab-${s.systemName}`}
                onClick={() => onBuild(s.systemId, s.systemName, buildArea)}>
                Építés: {s.systemName} ({AREA_META[buildArea].label})
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
